import React from "react";
import Link from "next/link";
import { Card, CardContent, CardHeader } from "@/components/cards/card/card";
import { ContentCard } from "@/types/cms";
import { renderPriceBody, renderValuePropsMarkdown } from "@/lib/richText";
import { Button } from "@/components/button/button/button";
import { Badge } from "@/components/badge/badge";
import CardOverlay from "@/components/cards/cardOverlay/cardOverlay";
import {
  normalizeCardButtons,
  splitButtons,
  type ButtonVisibility,
} from "@/components/cards/cardOverlay/buttonVisibility";
import { highlightFeatureDne } from "@/lib/featureDne";
import { formatPolarPriceText, cadenceLabel, type ResolvedPrice } from "@/lib/polarPriceFormat";
import styles from "./cardPrice.module.css";

interface CardPriceProps {
  card: ContentCard;
  freeVariant?: boolean;
}

const CardPrice = ({ card, freeVariant = false }: CardPriceProps) => {
  const blocksFromCard = Array.isArray(card.content)
    ? card.content
    : Array.isArray(card.body)
      ? card.body
      : undefined;
  const blocks = blocksFromCard;

  // Pricing fields now come from the CMS `price` relation on the card.
  // Keep rendering of the features list from `card.content` unchanged.
  const rawPrice = card.price as
    | { data?: { attributes?: Record<string, unknown> }; attributes?: Record<string, unknown> }
    | Record<string, unknown>
    | null
    | undefined;
  const priceAttrs: Record<string, unknown> | null =
    (rawPrice as { data?: { attributes?: Record<string, unknown> } })?.data?.attributes ??
    (rawPrice as { attributes?: Record<string, unknown> })?.attributes ??
    (rawPrice as Record<string, unknown>) ??
    null;

  const planTitle = (priceAttrs?.planTitle ?? "").toString();
  const planSubtitle = (priceAttrs?.planSubtitle ?? "").toString();
  const cmsPrice = (priceAttrs?.price ?? "").toString();
  const strikeoutPrice = (priceAttrs?.strikeoutPrice ?? "").toString();
  const planSavings = (priceAttrs?.savings ?? "").toString();
  const badgeText = (priceAttrs?.badge ?? "").toString().trim();

  // Live price from Polar (attached server-side by enrichSectionsWithPolarPrices
  // when the price has a `polarProductId`). Falls back to the CMS free-text
  // price. Polar is the source of truth for what's actually charged.
  const resolvedPrice = priceAttrs?.resolvedPrice as ResolvedPrice | undefined;
  const price = resolvedPrice ? formatPolarPriceText(resolvedPrice) : cmsPrice;
  // Muted cadence label next to the price: "/mo" / "One-Time". Prefer the live
  // Polar price (precise monthly vs yearly); when Polar isn't configured on the
  // landing, fall back to the card's pricing-tab tag (`contentCard_customID`,
  // the same signal the tabs bucket on) so the cadence still shows.
  const customId = (card?.contentCard_customID ?? "").toString().toLowerCase();
  const isOneTime = /one-?time|one_time|package/.test(customId);
  const isSubscription = customId.includes("subscription");
  const cadence = resolvedPrice
    ? cadenceLabel(resolvedPrice)
    : isSubscription
      ? "/mo"
      : isOneTime
        ? "One-Time"
        : "";

  // Shared value props (CMS): a `valuePropsTitle` heading + a `valueProps`
  // markdown string. When markdown is present it replaces the card's rich-text
  // feature list; otherwise the existing `content` renders.
  const valuePropsTitle = (priceAttrs?.valuePropsTitle ?? "").toString().trim();
  const valuePropsMarkdown = (priceAttrs?.valueProps ?? "").toString().trim();

  // The price relation's `planSubtitle` is rendered as sans-serif body
  // text directly under the title/price, above the CTA. Everything in
  // the rich-text body (paragraph, heading, list) renders under the CTA.

  // Resolve buttons → which display on the card vs the overlay.
  const allCardButtons = normalizeCardButtons(card.buttons);
  const { cardButtons, overlayButtons } = splitButtons(
    allCardButtons,
    card.buttonVisibility as ButtonVisibility | undefined
  );

  // Single-CTA shortcut: when the card has exactly one button (regardless
  // of visibility — including "hidden"), the whole card surface becomes
  // that button's link. Mirrors the behavior in CardContentItem so price
  // cards behave identically to default cards.
  const wholeCardCta =
    allCardButtons.length === 1 && allCardButtons[0].href
      ? allCardButtons[0]
      : null;

  // Multi-button fallback: when there's >1 CTA and no overlay, keep the
  // existing primary-CTA Link wrap (first card-button with a URL).
  const primaryButton =
    cardButtons.find((b) => b.href) ||
    (cardButtons.length > 0 ? cardButtons[0] : null);
  const primaryHref = primaryButton?.href || "";
  const primaryIsBlank = !!primaryButton?.isBlank;

  const cardInner = (
    <Card
      className={styles.root}
      data-variant={freeVariant ? "free" : undefined}
      data-ph-price-card
      data-ph-plan={planTitle}
      data-ph-tab={isSubscription ? "subscription" : isOneTime ? "one-time" : "both"}
    >
      {/* Badge: centered on the card's top edge, primary color — matches the
          app paywall's "Most Popular" badge. CMS-driven text (price.badge). */}
      {badgeText && (
        <Badge className={styles.planBadge}>
          {badgeText}
        </Badge>
      )}
      <CardHeader className={styles.header}>
        {/* Plan name (badge moved to the card top, above) */}
        {planTitle && (
          <div className={styles.titleRow}>
            <div className={styles.titleGroup}>
              <h3 className={`title-sm ${styles.planTitle}`}>
                {highlightFeatureDne(planTitle)}
              </h3>
            </div>
          </div>
        )}

        {/* Mirrors the app paywall: the price on the left, then a column with
            the strikeout + savings badge on top and the cadence
            ("/mo" / "One-Time") below. */}
        {(price || strikeoutPrice) && (
          <div className={styles.priceRow}>
            <p className={`accent-2xl ${styles.price}`}>
              {price}
            </p>
            <div className={styles.priceDetails}>
              {(strikeoutPrice || planSavings) && (
                <div className={styles.savingsRow}>
                  {strikeoutPrice && (
                    <p className={`body-xs ${styles.strikeoutPrice}`}>
                      {strikeoutPrice}
                    </p>
                  )}
                  {planSavings && (
                    <Badge variant="secondary" className={styles.planSavingsBadge}>
                      {planSavings}
                    </Badge>
                  )}
                </div>
              )}
              {cadence && (
                <span className={`body-xs ${styles.priceCadence}`}>{cadence}</span>
              )}
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent className={styles.content}>
        {/* Plan subtitle (from the price relation) sits under the
            title/price, above the CTA, muted (body-xs). The slot
            always renders — even empty — so the CTA stays aligned with
            sibling cards in a row. Min-height reserves two lines. */}
        <p className={`body-xs ${styles.subtitle}`}>
          {planSubtitle ? highlightFeatureDne(planSubtitle) : " "}
        </p>

        {/* On-card buttons. Visual <span> elements; the outer wrapper
            (Link or DialogTrigger) owns the click. */}
        {cardButtons.length > 0 && (
          <div className={styles.buttons}>
            {cardButtons.map((button, idx) => (
              <Button
                key={button.id ?? idx}
                variant={button.variant}
                size={button.size}
                asChild
                className={styles.ctaButton}
              >
                <span title={button.title}>
                  {highlightFeatureDne(button.copy || button.title || "")}
                </span>
              </Button>
            ))}
          </div>
        )}

        {/* Feature list under the CTA. Prefer the shared CMS value props
            (a `valuePropsTitle` heading + a `valueProps` markdown string, also
            consumed by the app paywall); fall back to the card's rich-text
            body (paragraph/heading/bullet list in authored order). */}
        {valuePropsMarkdown || valuePropsTitle ? (
          <div className={styles.body}>
            {valuePropsTitle ? (
              // `title-xs` to match the free card's heading, which renders via
              // renderPriceBody (it wraps headings in `title-xs` + bodyHeading).
              <h4 className={`title-xs ${styles.bodyHeading}`}>
                {highlightFeatureDne(valuePropsTitle)}
              </h4>
            ) : null}
            {valuePropsMarkdown
              ? renderValuePropsMarkdown(valuePropsMarkdown, {
                  bulletList: styles.bullets,
                  paragraph: styles.bodyParagraph,
                })
              : null}
          </div>
        ) : (
          Array.isArray(blocks) &&
          blocks.length > 0 && (
            <div className={styles.body}>
              {renderPriceBody(blocks, {
                paragraph: styles.bodyParagraph,
                heading: styles.bodyHeading,
                bulletList: styles.bullets,
              })}
            </div>
          )
        )}
      </CardContent>
    </Card>
  );

  // 1) Single CTA wins: whole card → that button's link, even when
  //    visibility is "hidden" or "all on overlay". Matches CardContentItem.
  if (wholeCardCta) {
    return (
      <Link
        href={wholeCardCta.href}
        target={wholeCardCta.isBlank ? "_blank" : undefined}
        rel={wholeCardCta.isBlank ? "noopener noreferrer" : undefined}
        title={wholeCardCta.title}
        aria-label={planTitle || wholeCardCta.copy || wholeCardCta.title}
        className={styles.clickable}
      >
        {cardInner}
      </Link>
    );
  }

  // 2) Overlay wins: multi-button card with overlay slots → whole card
  //    opens the overlay.
  if (overlayButtons.length > 0) {
    return (
      <CardOverlay
        buttons={overlayButtons}
        title={planTitle || undefined}
        trigger={
          <div
            role="button"
            tabIndex={0}
            className={styles.clickable}
            aria-label={planTitle ? `Open ${planTitle} options` : "Open options"}
          >
            {cardInner}
          </div>
        }
      />
    );
  }

  // 3) Multi-button card with no overlay → fall back to the primary CTA
  //    link wrap (first card-button with a URL).
  if (primaryButton && primaryHref) {
    return (
      <Link
        href={primaryHref}
        target={primaryIsBlank ? "_blank" : undefined}
        rel={primaryIsBlank ? "noopener noreferrer" : undefined}
        className={styles.clickable}
      >
        {cardInner}
      </Link>
    );
  }

  // 4) No buttons / no URL → non-clickable card.
  return cardInner;
};

export default CardPrice;
